import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ComunicacionDetalle,
  ComunicacionListResponse,
  ComunicacionPayload,
  ComunicacionStats,
  DocumentosFirmaResponse,
  DocumentoParaFirma,
  DestinatarioMin,
  DomicilioPostal,
  EstadoComunicacion,
  FirmaAutomaticaStatus,
  ProcedimientoRef,
  RemesaConfig,
  TipoEnvio
} from './comunicacion.model';

@Injectable({ providedIn: 'root' })
export class GestionRemesasService {

  private readonly API_URL: string = environment.API_URL;

  constructor(private http: HttpClient) {}

  list(opts: {
    page?: number; size?: number; search?: string;
    estado?: EstadoComunicacion | 'todos';
    tipoEnvio?: number | 'todas';
  }): Observable<ComunicacionListResponse> {
    let params = new HttpParams()
      .set('page', String(opts.page ?? 0))
      .set('size', String(opts.size ?? 10));
    if (opts.search && opts.search.trim()) params = params.set('search', opts.search.trim());
    if (opts.estado && opts.estado !== 'todos') params = params.set('estado', opts.estado);
    if (opts.tipoEnvio != null && opts.tipoEnvio !== 'todas') params = params.set('tipoEnvio', String(opts.tipoEnvio));
    return this.http.get<ComunicacionListResponse>(`${this.API_URL}/notifica/remesa/list`, { params });
  }

  stats(): Observable<ComunicacionStats> {
    return this.http.get<ComunicacionStats>(`${this.API_URL}/notifica/remesa/stats`);
  }

  tipos(): Observable<TipoEnvio[]> {
    return this.http.get<TipoEnvio[]>(`${this.API_URL}/notifica/remesa/tipos`);
  }

  /**
   * Trámites de la aplicación con SIA (para asociar a remesas Notifica).
   * No usa el catálogo legacy {@code /notifica/procedimientos}.
   */
  procedimientos(): Observable<ProcedimientoRef[]> {
    return this.http.get<any>(`${this.API_URL}/procedimiento/list`).pipe(
      map(resp => {
        const list = Array.isArray(resp) ? resp : (resp?.Procedimiento || []);
        return (list || []).filter((p: ProcedimientoRef) => p?.sia != null && p.sia > 0);
      })
    );
  }

  getById(id: number): Observable<ComunicacionDetalle> {
    return this.http.get<ComunicacionDetalle>(`${this.API_URL}/notifica/remesa/${id}`);
  }

  save(payload: ComunicacionPayload): Observable<any> {
    return this.http.post(`${this.API_URL}/notifica/remesa/save`, payload);
  }

  send(id: number): Observable<any> {
    return this.http.post(`${this.API_URL}/notifica/remesa/${id}/send`, {});
  }

  prepararDocumentosFirma(id: number): Observable<DocumentosFirmaResponse> {
    return this.http.post<DocumentosFirmaResponse>(`${this.API_URL}/notifica/remesa/${id}/preparar-firma`, {});
  }

  guardarDocumentosFirmados(id: number, documentos: DocumentoParaFirma[]): Observable<any> {
    return this.http.post(`${this.API_URL}/notifica/remesa/${id}/documentos-firmados`, { documentos });
  }

  schedule(id: number, fechaEnvio: string): Observable<any> {
    return this.http.post(`${this.API_URL}/notifica/remesa/${id}/schedule`, { fechaEnvio });
  }

  duplicate(id: number): Observable<any> {
    return this.http.post(`${this.API_URL}/notifica/remesa/${id}/duplicate`, {});
  }

  delete(id: number): Observable<any> {
    return this.http.delete(`${this.API_URL}/notifica/remesa/${id}`);
  }

  /* Descarga un documento del gestor documental en base64 (`{ Result: b64 }`). */
  downloadB64(idGestor: string): Observable<{ Result: string }> {
    return this.http.get<{ Result: string }>(`${this.API_URL}/nextcloud/downloadB64/${idGestor}`);
  }

  /* Comprueba si el sello del organismo está configurado para firmar documentos de plantilla. */
  firmaAutomatica(): Observable<FirmaAutomaticaStatus> {
    return this.http.get<FirmaAutomaticaStatus>(`${this.API_URL}/notifica/remesa/firma-automatica`);
  }

  firmantes(): Observable<DestinatarioMin[]> {
    return this.http.get<DestinatarioMin[]>(`${this.API_URL}/notifica/remesa/firmantes`);
  }

  solicitarFirma(id: number, firmanteId: number): Observable<any> {
    return this.http.post(`${this.API_URL}/notifica/remesa/${id}/solicitar-firma`, { firmanteId });
  }

  /** Flags de remesa (Excel de destinatarios externos, etc.). */
  config(): Observable<RemesaConfig> {
    return this.http.get<RemesaConfig>(`${this.API_URL}/notifica/remesa/config`);
  }

  /** Domicilio de ficha de usuarios de la aplicación. */
  domicilios(usuarioIds: number[]): Observable<Record<string, DomicilioPostal>> {
    return this.http.post<Record<string, DomicilioPostal>>(
      `${this.API_URL}/notifica/remesa/domicilios`,
      { usuarioIds }
    );
  }
}
